import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit2, Trash2, Calendar, User, ChevronDown, Search, LayoutGrid, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuthState";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Form schemas
const roadmapItemSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  status: z.string().default("planned"),
  priority: z.string().default("medium"),
  category: z.string().default("feature"),
  targetDate: z.string().optional(),
  assignedTo: z.number().optional(),
});

const bugSchema = z.object({
  title: z.string().min(1, "Bug title is required"),
  description: z.string().min(1, "Bug description is required"),
  severity: z.string().default("medium"),
  status: z.string().default("open"),
  priority: z.string().default("medium"),
  reproductionSteps: z.string().optional(),
  expectedBehavior: z.string().optional(),
  actualBehavior: z.string().optional(),
  environment: z.string().optional(),
});

const testCaseSchema = z.object({
  title: z.string().min(1, "Test case title is required"),
  description: z.string().optional(),
  feature: z.string().min(1, "Feature is required"),
  priority: z.string().default("medium"),
  status: z.string().default("active"),
  testSteps: z.string().optional(),
  expectedResults: z.string().optional(),
  preconditions: z.string().optional(),
  type: z.string().default("functional"),
  estimatedTime: z.number().optional(),
  automationStatus: z.string().default("manual"),
});

const testRunSchema = z.object({
  name: z.string().min(1, "Test run name is required"),
  description: z.string().optional(),
  environment: z.string().optional(),
  status: z.string().default("planned"),
  version: z.string().optional(),
});

type RoadmapItemForm = z.infer<typeof roadmapItemSchema>;
type BugForm = z.infer<typeof bugSchema>;
type TestCaseForm = z.infer<typeof testCaseSchema>;
type TestRunForm = z.infer<typeof testRunSchema>;

// Types
interface AdminUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  username: string;
}

interface RoadmapItem {
  id: number;
  title: string;
  description?: string;
  status: string;
  priority: string;
  category: string;
  targetDate?: string;
  completedDate?: string;
  assignedTo?: number;
  tags?: string[];
  createdBy?: number;
  createdByUser?: AdminUser;
  createdAt: string;
  updatedAt: string;
}

interface Bug {
  id: number;
  title: string;
  description: string;
  severity: string;
  status: string;
  priority: string;
  reproductionSteps?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  environment?: string;
  reportedBy?: number;
  reportedByUser?: AdminUser;
  assignedTo?: number;
  assignedToUser?: AdminUser;
  createdAt: string;
  updatedAt: string;
}

interface TestCase {
  id: number;
  title: string;
  description?: string;
  feature: string;
  priority: string;
  status: string;
  testSteps?: string;
  expectedResults?: string;
  preconditions?: string;
  type?: string;
  estimatedTime?: number;
  automationStatus?: string;
  createdBy?: number;
  createdByUser?: AdminUser;
  assignedTo?: number;
  assignedToUser?: AdminUser;
  createdAt: string;
  updatedAt: string;
}

interface TestRun {
  id: number;
  name: string;
  description?: string;
  environment: string;
  status: string;
  version?: string;
  plannedDate?: string;
  startedAt?: string;
  completedAt?: string;
  executedBy?: number;
  createdAt: string;
}

// Color mappings
const statusColors = {
  // Roadmap statuses
  planned: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  in_progress: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  backlog: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  
  // Bug statuses
  open: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  assigned: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  
  // Test statuses
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  inactive: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  deprecated: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  
  // Test run statuses
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

const priorityColors = {
  low: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  high: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  critical: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

// Status configurations for each tab type
const ROADMAP_STATUSES = [
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "backlog", label: "Backlog" },
];

const BUG_STATUSES = [
  { value: "open", label: "Open" },
  { value: "assigned", label: "Assigned" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const TESTCASE_STATUSES = [
  { value: "active", label: "TO DO" },
  { value: "inactive", label: "PLANNING" },
  { value: "deprecated", label: "ON HOLD" },
  { value: "ongoing", label: "ONGOING" },
  { value: "completed", label: "COMPLETED" },
];

const TESTRUN_STATUSES = [
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

// Sortable Item Component
interface SortableItemProps {
  id: string | number;
  children: React.ReactNode;
}

function SortableItem({ id, children }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing"
    >
      {children}
    </div>
  );
}

// Item Card Component
interface ItemCardProps {
  item: RoadmapItem | Bug | TestCase | TestRun;
  onEdit: () => void;
  onDelete: () => void;
  onView?: () => void;
  type: "roadmap" | "bug" | "testcase" | "testrun";
}

function ItemCard({ item, onEdit, onDelete, onView, type }: ItemCardProps) {
  const handleCardClick = () => {
    if (onView) {
      onView();
    }
  };

  return (
    <Card
      className="hover:shadow-lg transition-all duration-200 cursor-pointer border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      onClick={handleCardClick}
    >
      <CardContent className="p-3">
        <div className="space-y-3">
          {/* Header with title and actions */}
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 flex-1">
              {type === "testrun" ? (item as TestRun).name : item.title}
            </h4>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                  }}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Description */}
          {"description" in item && item.description && (
            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
              {item.description}
            </p>
          )}

          {/* Badges */}
          <div className="flex flex-wrap gap-1.5">
            {"priority" in item && (
              <Badge className={`text-xs px-2 py-0.5 ${priorityColors[item.priority as keyof typeof priorityColors]}`}>
                {item.priority}
              </Badge>
            )}
            {"category" in item && (
              <Badge variant="outline" className="text-xs px-2 py-0.5">
                {item.category}
              </Badge>
            )}
            {"severity" in item && (
              <Badge variant="outline" className="text-xs px-2 py-0.5">
                {item.severity}
              </Badge>
            )}
            {"type" in item && item.type && (
              <Badge variant="outline" className="text-xs px-2 py-0.5">
                {item.type}
              </Badge>
            )}
            {"automationStatus" in item && item.automationStatus && (
              <Badge variant="outline" className="text-xs px-2 py-0.5">
                {item.automationStatus}
              </Badge>
            )}
            {"environment" in item && item.environment && (
              <Badge variant="outline" className="text-xs px-2 py-0.5">
                {item.environment}
              </Badge>
            )}
          </div>

          {/* Footer metadata */}
          <div className="flex flex-col gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-2 flex-wrap">
              {"estimatedTime" in item && item.estimatedTime && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{item.estimatedTime}m</span>
                </div>
              )}
              {"targetDate" in item && item.targetDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{new Date(item.targetDate).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            
            {/* Created By / Reported By */}
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>
                {"createdByUser" in item && item.createdByUser ? (
                  <>Created by: {getUserFullName(item.createdByUser)}</>
                ) : "reportedByUser" in item && item.reportedByUser ? (
                  <>Reported by: {getUserFullName(item.reportedByUser)}</>
                ) : (
                  <>Created by: Unknown</>
                )}
              </span>
            </div>

            {/* Assigned To */}
            {("assignedToUser" in item && item.assignedToUser) && (
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>Assigned to: {getUserFullName(item.assignedToUser)}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Droppable Column Component
interface DroppableColumnProps {
  id: string;
  children: React.ReactNode;
}

function DroppableColumn({ id, children }: DroppableColumnProps) {
  const { setNodeRef } = useSortable({
    id: id,
    data: {
      type: 'column',
    },
  });

  return (
    <div ref={setNodeRef} className="flex-1 min-h-[400px]">
      {children}
    </div>
  );
}

// Board View Component
interface BoardViewProps {
  items: any[];
  statuses: { value: string; label: string }[];
  onStatusChange: (itemId: number, newStatus: string) => void;
  onEdit: (item: any) => void;
  onDelete: (itemId: number) => void;
  onView?: (item: any) => void;
  onAdd?: (status: string) => void;
  type: "roadmap" | "bug" | "testcase" | "testrun";
}

function BoardView({ items, statuses, onStatusChange, onEdit, onDelete, onView, onAdd, type }: BoardViewProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const itemsByStatus = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    statuses.forEach(status => {
      grouped[status.value] = items.filter(item => item.status === status.value);
    });
    return grouped;
  }, [items, statuses]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    // Check if we're dragging over a different column
    const activeItem = items.find(item => item.id.toString() === activeId);
    if (!activeItem) return;

    // If dragging over a column (status), update immediately for visual feedback
    if (statuses.some(s => s.value === overId)) {
      // This is a column droppable zone
      return;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeItem = items.find(item => item.id.toString() === active.id.toString());
    if (!activeItem) {
      setActiveId(null);
      return;
    }

    const overId = over.id.toString();
    
    // Check if dropped on a column (status value)
    const targetStatus = statuses.find(s => s.value === overId);
    if (targetStatus && activeItem.status !== targetStatus.value) {
      onStatusChange(activeItem.id, targetStatus.value);
    } else {
      // Dropped on an item, find which column it belongs to
      const overItem = items.find(item => item.id.toString() === overId);
      if (overItem && activeItem.status !== overItem.status) {
        onStatusChange(activeItem.id, overItem.status);
      }
    }

    setActiveId(null);
  };

  const activeItem = activeId ? items.find(item => item.id.toString() === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        <SortableContext items={statuses.map(s => s.value)} strategy={verticalListSortingStrategy}>
          {statuses.map(status => (
            <div key={status.value} className="flex flex-col min-w-[280px] w-[280px] flex-shrink-0">
              {/* Column Header */}
              <div className="mb-3 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-t-lg border-b-2 border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    {status.label}
                  </h3>
                  <Badge variant="secondary" className="ml-2 bg-gray-200 dark:bg-gray-700">
                    {itemsByStatus[status.value]?.length || 0}
                  </Badge>
                </div>
              </div>
              
              {/* Droppable Column Area */}
              <SortableContext
                items={itemsByStatus[status.value]?.map(item => item.id.toString()) || []}
                strategy={verticalListSortingStrategy}
                id={status.value}
              >
                <DroppableColumn id={status.value}>
                  <div 
                    className="min-h-[400px] bg-gray-50 dark:bg-gray-900/50 rounded-b-lg p-3 space-y-3 border-2 border-dashed border-gray-200 dark:border-gray-700"
                  >
                    {itemsByStatus[status.value]?.map(item => (
                      <SortableItem key={item.id} id={item.id}>
                        <ItemCard
                          item={item}
                          onEdit={() => onEdit(item)}
                          onDelete={() => onDelete(item.id)}
                          onView={() => onView?.(item)}
                          type={type}
                        />
                      </SortableItem>
                    ))}
                    {(itemsByStatus[status.value]?.length || 0) === 0 && (
                      <div className="text-center py-12 text-gray-400 text-sm">
                        Drop items here
                      </div>
                    )}
                    {onAdd && (
                      <div className="pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-left text-sm text-primary w-full"
                          onClick={() => onAdd(status.value)}
                        >
                          + Add Item
                        </Button>
                      </div>
                    )}
                  </div>
                </DroppableColumn>
              </SortableContext>
            </div>
          ))}
        </SortableContext>
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="opacity-90 rotate-3 scale-105 shadow-2xl">
            <ItemCard
              item={activeItem}
              onEdit={() => {}}
              onDelete={() => {}}
              onView={() => {}}
              type={type}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// List View Component
interface ListViewProps {
  items: any[];
  statuses: { value: string; label: string }[];
  onEdit: (item: any) => void;
  onDelete: (itemId: number) => void;
  onStatusChange: (itemId: number, newStatus: string) => void;
  type: "roadmap" | "bug" | "testcase" | "testrun";
  onView?: (item: any) => void;
}

function ListView({ items, statuses, onEdit, onDelete, onStatusChange, onView, type }: ListViewProps) {
  return (
    <div className="space-y-2">
      {items.map(item => (
        <Card
          key={item.id}
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onView?.(item)}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base">
                      {type === "testrun" ? (item as TestRun).name : item.title}
                    </h3>
                    {"description" in item && item.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1 mt-1">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          onEdit(item);
                        }}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(item.id);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
              </div>
              <div className="flex flex-wrap gap-2">
                <Select
                  value={item.status}
                  onValueChange={(value) => onStatusChange(item.id, value)}
                >
                  <SelectTrigger
                    className="w-40"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(status => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {"priority" in item && (
                  <Badge className={priorityColors[item.priority as keyof typeof priorityColors]}>
                    {item.priority}
                  </Badge>
                )}
                {"category" in item && <Badge variant="outline">{item.category}</Badge>}
                {"severity" in item && <Badge variant="outline">{item.severity}</Badge>}
                {"type" in item && item.type && <Badge variant="outline">{item.type}</Badge>}
                {"automationStatus" in item && item.automationStatus && (
                  <Badge variant="outline">{item.automationStatus}</Badge>
                )}
                {"environment" in item && item.environment && (
                  <Badge variant="outline">{item.environment}</Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      ))}
      {items.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500">No items found.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
// Utility function to get user's full name
const getUserFullName = (user?: AdminUser | null) => {
  if (!user) return "Unknown";
  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || user.username || user.email;
};

// Roadmap Items Tab with View Toggle
function RoadmapItemsTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RoadmapItem | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"board" | "list">("board");
  const [activeSubTab, setActiveSubTab] = useState("all");
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedDetailItem, setSelectedDetailItem] = useState<RoadmapItem | null>(null);

  const { data: items = [], isLoading } = useQuery<RoadmapItem[]>({
    queryKey: ["/api/roadmap"],
  });

  // Fetch admin users for assignment dropdown
  const { data: adminUsers = [] } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/admins"],
  });

  const form = useForm<RoadmapItemForm>({
    resolver: zodResolver(roadmapItemSchema),
    defaultValues: {
      title: "",
      description: "",
      status: "planned",
      priority: "medium",
      category: "feature",
      targetDate: "",
      assignedTo: undefined,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: RoadmapItemForm) => {
      const response = await apiRequest("POST", "/api/roadmap", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/roadmap"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({ title: "Roadmap item created successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to create roadmap item", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<RoadmapItemForm> }) => {
      const response = await apiRequest("PUT", `/api/roadmap/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/roadmap"] });
      setEditingItem(null);
      form.reset();
      toast({ title: "Roadmap item updated successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to update roadmap item", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/roadmap/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/roadmap"] });
      toast({ title: "Roadmap item deleted successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to delete roadmap item", description: error.message, variant: "destructive" });
    },
  });

  const onSubmit = (data: RoadmapItemForm) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (item: RoadmapItem) => {
    setEditingItem(item);
    form.reset({
      title: item.title,
      description: item.description || "",
      status: item.status,
      priority: item.priority,
      category: item.category,
      targetDate: item.targetDate ? new Date(item.targetDate).toISOString().split('T')[0] : "",
      assignedTo: item.assignedTo || undefined,
    });
    setIsCreateDialogOpen(true);
  };

  const handleStatusChange = (itemId: number, newStatus: string) => {
    updateMutation.mutate({ id: itemId, data: { status: newStatus } });
  };

  const handleViewDetails = (item: RoadmapItem) => {
    setSelectedDetailItem(item);
    setIsDetailDialogOpen(true);
  };

  const handleAddForStatus = (status: string) => {
    setEditingItem(null);
    form.reset({
      title: "",
      description: "",
      status,
      priority: "medium",
      category: "feature",
      targetDate: "",
      assignedTo: undefined,
    });
    setIsCreateDialogOpen(true);
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = activeSubTab === "all" || item.status === activeSubTab;
      return matchesSearch && matchesStatus;
    });
  }, [items, searchTerm, activeSubTab]);

  return (
    <div className="space-y-4">
      {/* Sub-tabs for status */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {ROADMAP_STATUSES.map(status => (
              <TabsTrigger key={status.value} value={status.value}>
                {status.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === "board" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("board")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingItem(null); form.reset(); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? "Edit Roadmap Item" : "Create Roadmap Item"}</DialogTitle>
                  <DialogDescription>
                    {editingItem ? "Update the roadmap item details." : "Add a new item to the roadmap."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      {...form.register("title")}
                      placeholder="Enter roadmap item title"
                    />
                    {form.formState.errors.title && (
                      <p className="text-sm text-red-500 mt-1">{form.formState.errors.title.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      {...form.register("description")}
                      placeholder="Enter description"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select onValueChange={(value) => form.setValue("status", value)} value={form.watch("status")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROADMAP_STATUSES.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select onValueChange={(value) => form.setValue("priority", value)} value={form.watch("priority")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select onValueChange={(value) => form.setValue("category", value)} value={form.watch("category")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="feature">Feature</SelectItem>
                          <SelectItem value="improvement">Improvement</SelectItem>
                          <SelectItem value="task">Task</SelectItem>
                          <SelectItem value="research">Research</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="targetDate">Target Date</Label>
                      <Input
                        id="targetDate"
                        type="date"
                        {...form.register("targetDate")}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="assignedTo">Assigned To (Admin Only)</Label>
                    <Select 
                      onValueChange={(value) => {
                        if (value === "unassigned") {
                          form.setValue("assignedTo", undefined);
                        } else {
                          form.setValue("assignedTo", parseInt(value));
                        }
                      }} 
                      value={form.watch("assignedTo")?.toString() || "unassigned"}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select admin to assign" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {adminUsers.map((admin) => (
                          <SelectItem key={admin.id} value={admin.id.toString()}>
                            {getUserFullName(admin)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editingItem ? "Update" : "Create"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <TabsContent value={activeSubTab} className="mt-0">
          {isLoading ? (
            <div className="text-center py-8">Loading roadmap items...</div>
          ) : viewMode === "board" ? (
            <BoardView
              items={filteredItems}
              statuses={ROADMAP_STATUSES}
              onStatusChange={handleStatusChange}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onView={handleViewDetails}
              onAdd={handleAddForStatus}
              type="roadmap"
            />
          ) : (
            <ListView
              items={filteredItems}
              statuses={ROADMAP_STATUSES}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onStatusChange={handleStatusChange}
              onView={handleViewDetails}
              type="roadmap"
            />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedDetailItem?.title || "Roadmap Item"}</DialogTitle>
            <DialogDescription>
              {selectedDetailItem?.description || "No additional details provided."}
            </DialogDescription>
          </DialogHeader>
          {selectedDetailItem && (
            <div className="space-y-3 py-2 text-sm text-gray-700">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase text-gray-500">Status</p>
                  <p className="font-medium">{selectedDetailItem.status}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Priority</p>
                  <p className="font-medium">{selectedDetailItem.priority}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Category</p>
                  <p className="font-medium">{selectedDetailItem.category}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Target Date</p>
                  <p className="font-medium">
                    {selectedDetailItem.targetDate
                      ? new Date(selectedDetailItem.targetDate).toLocaleDateString()
                      : "TBD"}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Assigned To</p>
                <p className="font-medium">
                  {selectedDetailItem.assignedTo ? `Admin ID ${selectedDetailItem.assignedTo}` : "Unassigned"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Created By</p>
                <p className="font-medium">
                  {selectedDetailItem.createdBy ? `Admin ID ${selectedDetailItem.createdBy}` : "Unknown"}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                if (selectedDetailItem) {
                  handleEdit(selectedDetailItem);
                }
                setIsDetailDialogOpen(false);
              }}
            >
              Edit Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Bugs Tab with View Toggle
function BugsTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingBug, setEditingBug] = useState<Bug | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"board" | "list">("board");
  const [activeSubTab, setActiveSubTab] = useState("all");
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedBugDetail, setSelectedBugDetail] = useState<Bug | null>(null);

  const { data: bugs = [], isLoading } = useQuery<Bug[]>({
    queryKey: ["/api/bugs"],
  });

  const bugFormDefaultValues: BugForm = {
    title: "",
    description: "",
    severity: "medium",
    status: "open",
    priority: "medium",
    reproductionSteps: "",
    expectedBehavior: "",
    actualBehavior: "",
    environment: "",
  };

  const form = useForm<BugForm>({
    resolver: zodResolver(bugSchema),
    defaultValues: bugFormDefaultValues,
  });

  const createMutation = useMutation({
    mutationFn: async (data: BugForm) => {
      const response = await apiRequest("POST", "/api/bugs", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bugs"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({ title: "Bug reported successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to report bug", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<BugForm> }) => {
      const response = await apiRequest("PUT", `/api/bugs/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bugs"] });
      setEditingBug(null);
      form.reset();
      toast({ title: "Bug updated successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to update bug", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/bugs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bugs"] });
      toast({ title: "Bug deleted successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to delete bug", description: error.message, variant: "destructive" });
    },
  });

  const onSubmit = (data: BugForm) => {
    if (editingBug) {
      updateMutation.mutate({ id: editingBug.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (bug: Bug) => {
    setEditingBug(bug);
    form.reset({
      title: bug.title || "",
      description: bug.description || "",
      severity: bug.severity || "medium",
      status: bug.status || "open",
      priority: bug.priority || "medium",
      reproductionSteps: bug.reproductionSteps || "",
      expectedBehavior: bug.expectedBehavior || "",
      actualBehavior: bug.actualBehavior || "",
      environment: bug.environment || "",
    });
    setIsCreateDialogOpen(true);
  };

  const handleStatusChange = (bugId: number, newStatus: string) => {
    updateMutation.mutate({ id: bugId, data: { status: newStatus } });
  };

  const handleViewBug = (bug: Bug) => {
    setSelectedBugDetail(bug);
    setIsDetailDialogOpen(true);
  };

  const handleAddForStatus = (status: string) => {
    setEditingBug(null);
    form.reset({ ...bugFormDefaultValues, status });
    setIsCreateDialogOpen(true);
  };

  const closeBugDetailDialog = () => {
    setIsDetailDialogOpen(false);
    setSelectedBugDetail(null);
  };

  const filteredBugs = useMemo(() => {
    return bugs.filter((bug) => {
      const matchesSearch = bug.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           bug.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = activeSubTab === "all" || bug.status === activeSubTab;
      return matchesSearch && matchesStatus;
    });
  }, [bugs, searchTerm, activeSubTab]);

  return (
    <div className="space-y-4">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {BUG_STATUSES.map(status => (
              <TabsTrigger key={status.value} value={status.value}>
                {status.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === "board" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("board")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingBug(null); form.reset(); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Report Bug
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingBug ? "Edit Bug" : "Report New Bug"}</DialogTitle>
                  <DialogDescription>
                    {editingBug ? "Update the bug details." : "Provide detailed information about the bug."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Bug Title</Label>
                    <Input
                      id="title"
                      {...form.register("title")}
                      placeholder="Brief description of the bug"
                    />
                    {form.formState.errors.title && (
                      <p className="text-sm text-red-500 mt-1">{form.formState.errors.title.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      {...form.register("description")}
                      placeholder="Detailed description of the bug"
                      rows={3}
                    />
                    {form.formState.errors.description && (
                      <p className="text-sm text-red-500 mt-1">{form.formState.errors.description.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="severity">Severity</Label>
                      <Select onValueChange={(value) => form.setValue("severity", value)} value={form.watch("severity")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select severity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select onValueChange={(value) => form.setValue("status", value)} value={form.watch("status")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUG_STATUSES.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select onValueChange={(value) => form.setValue("priority", value)} value={form.watch("priority")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="reproductionSteps">Steps to Reproduce</Label>
                    <Textarea
                      id="reproductionSteps"
                      {...form.register("reproductionSteps")}
                      placeholder="1. Go to...&#10;2. Click on...&#10;3. Expected result vs actual result"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="expectedBehavior">Expected Behavior</Label>
                      <Textarea
                        id="expectedBehavior"
                        {...form.register("expectedBehavior")}
                        placeholder="What should happen"
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label htmlFor="actualBehavior">Actual Behavior</Label>
                      <Textarea
                        id="actualBehavior"
                        {...form.register("actualBehavior")}
                        placeholder="What actually happens"
                        rows={2}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="environment">Environment</Label>
                    <Input
                      id="environment"
                      {...form.register("environment")}
                      placeholder="Browser, OS, device details"
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editingBug ? "Update Bug" : "Report Bug"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <TabsContent value={activeSubTab} className="mt-0">
          {isLoading ? (
            <div className="text-center py-8">Loading bugs...</div>
          ) : viewMode === "board" ? (
            <BoardView
              items={filteredBugs}
              statuses={BUG_STATUSES}
              onStatusChange={handleStatusChange}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onView={handleViewBug}
              onAdd={handleAddForStatus}
              type="bug"
            />
          ) : (
            <ListView
              items={filteredBugs}
              statuses={BUG_STATUSES}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onStatusChange={handleStatusChange}
              onView={handleViewBug}
              type="bug"
            />
          )}
        </TabsContent>
      </Tabs>
      <Dialog open={isDetailDialogOpen} onOpenChange={(open) => !open && closeBugDetailDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedBugDetail?.title || "Bug Report"}</DialogTitle>
            <DialogDescription>
              {selectedBugDetail?.description || "No description provided for this bug."}
            </DialogDescription>
          </DialogHeader>
          {selectedBugDetail && (
            <div className="space-y-3 py-2 text-sm text-gray-700">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase text-gray-500">Status</p>
                  <p className="font-medium">{selectedBugDetail.status}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Severity</p>
                  <p className="font-medium">{selectedBugDetail.severity}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Priority</p>
                  <p className="font-medium">{selectedBugDetail.priority}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Environment</p>
                  <p className="font-medium">{selectedBugDetail.environment || "Not specified"}</p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Reproduction Steps</p>
                <p className="font-medium whitespace-pre-line">
                  {selectedBugDetail.reproductionSteps || "Not provided"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Expected Behavior</p>
                <p className="font-medium whitespace-pre-line">
                  {selectedBugDetail.expectedBehavior || "Not provided"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Actual Behavior</p>
                <p className="font-medium whitespace-pre-line">
                  {selectedBugDetail.actualBehavior || "Not provided"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Reported By</p>
                <p className="font-medium">
                  {selectedBugDetail.reportedByUser
                    ? getUserFullName(selectedBugDetail.reportedByUser)
                    : selectedBugDetail.reportedBy
                      ? `Admin ID ${selectedBugDetail.reportedBy}`
                      : "Unknown"}
                </p>
              </div>
              {selectedBugDetail.assignedToUser || selectedBugDetail.assignedTo ? (
                <div>
                  <p className="text-xs uppercase text-gray-500">Assigned To</p>
                  <p className="font-medium">
                    {selectedBugDetail.assignedToUser
                      ? getUserFullName(selectedBugDetail.assignedToUser)
                      : `Admin ID ${selectedBugDetail.assignedTo}`}
                  </p>
                </div>
              ) : null}
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={closeBugDetailDialog}>
              Close
            </Button>
            <Button
              onClick={() => {
                if (selectedBugDetail) {
                  handleEdit(selectedBugDetail);
                }
                closeBugDetailDialog();
              }}
            >
              Edit Bug
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Test Cases Tab with View Toggle
function TestCasesTab() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"board" | "list">("board");
  const [activeSubTab, setActiveSubTab] = useState("all");
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState<TestCase | null>(null);

  const { data: testCases = [], isLoading } = useQuery<TestCase[]>({
    queryKey: ["/api/testcases"],
  });

  const form = useForm<TestCaseForm>({
    resolver: zodResolver(testCaseSchema),
    defaultValues: {
      title: "",
      description: "",
      feature: "",
      priority: "medium",
      status: "active",
      testSteps: "",
      expectedResults: "",
      preconditions: "",
      type: "functional",
      estimatedTime: 0,
      automationStatus: "manual",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: TestCaseForm) => {
      const response = await apiRequest("POST", "/api/testcases", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testcases"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({ title: "Test case created successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to create test case", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<TestCaseForm> }) => {
      const response = await apiRequest("PUT", `/api/testcases/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testcases"] });
      setEditingTestCase(null);
      form.reset();
      toast({ title: "Test case updated successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to update test case", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/testcases/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testcases"] });
      toast({ title: "Test case deleted successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to delete test case", description: error.message, variant: "destructive" });
    },
  });

  const onSubmit = (data: TestCaseForm) => {
    if (editingTestCase) {
      updateMutation.mutate({ id: editingTestCase.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (testCase: TestCase) => {
    setEditingTestCase(testCase);
    form.reset({
      title: testCase.title || "",
      description: testCase.description || "",
      feature: testCase.feature || "",
      priority: testCase.priority || "medium",
      status: testCase.status || "active",
      testSteps: testCase.testSteps || "",
      expectedResults: testCase.expectedResults || "",
      preconditions: testCase.preconditions || "",
      type: testCase.type || "functional",
      estimatedTime: testCase.estimatedTime || 0,
      automationStatus: testCase.automationStatus || "manual",
    });
    setIsCreateDialogOpen(true);
  };

  const handleViewTestCase = (testCase: TestCase) => {
    setSelectedTestCase(testCase);
    setIsDetailDialogOpen(true);
  };

  const handleStatusChange = (testCaseId: number, newStatus: string) => {
    updateMutation.mutate({ id: testCaseId, data: { status: newStatus } });
  };

  const handleAddForStatus = (status: string) => {
    setEditingTestCase(null);
    form.reset({
      title: "",
      description: "",
      feature: "",
      priority: "medium",
      status,
      testSteps: "",
      expectedResults: "",
      preconditions: "",
      type: "functional",
      estimatedTime: 0,
      automationStatus: "manual",
    });
    setIsCreateDialogOpen(true);
  };

  const closeTestCaseDialog = () => {
    setIsDetailDialogOpen(false);
    setSelectedTestCase(null);
  };

  const filteredTestCases = useMemo(() => {
    return testCases.filter((testCase) => {
      const matchesSearch = testCase.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           testCase.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = activeSubTab === "all" || testCase.status === activeSubTab;
      return matchesSearch && matchesStatus;
    });
  }, [testCases, searchTerm, activeSubTab]);

  return (
    <div className="space-y-4">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {TESTCASE_STATUSES.map(status => (
              <TabsTrigger key={status.value} value={status.value}>
                {status.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === "board" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("board")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingTestCase(null); form.reset(); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Test Case
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingTestCase ? "Edit Test Case" : "Create Test Case"}</DialogTitle>
                  <DialogDescription>
                    {editingTestCase ? "Update the test case details." : "Create a new test case for validation."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Test Case Title</Label>
                    <Input
                      id="title"
                      {...form.register("title")}
                      placeholder="Brief description of what to test"
                    />
                    {form.formState.errors.title && (
                      <p className="text-sm text-red-500 mt-1">{form.formState.errors.title.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      {...form.register("description")}
                      placeholder="Detailed description of the test case"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="type">Category</Label>
                      <Select onValueChange={(value) => form.setValue("type", value)} value={form.watch("type")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="functional">Functional</SelectItem>
                          <SelectItem value="ui">UI/UX</SelectItem>
                          <SelectItem value="integration">Integration</SelectItem>
                          <SelectItem value="performance">Performance</SelectItem>
                          <SelectItem value="security">Security</SelectItem>
                          <SelectItem value="api">API</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select onValueChange={(value) => form.setValue("priority", value)} value={form.watch("priority")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select onValueChange={(value) => form.setValue("status", value)} value={form.watch("status")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {TESTCASE_STATUSES.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="automationStatus">Automation Status</Label>
                      <Select onValueChange={(value) => form.setValue("automationStatus", value)} value={form.watch("automationStatus")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="semi-automated">Semi-automated</SelectItem>
                          <SelectItem value="automated">Automated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="feature">Feature</Label>
                    <Input
                      id="feature"
                      {...form.register("feature")}
                      placeholder="Feature being tested"
                    />
                    {form.formState.errors.feature && (
                      <p className="text-sm text-red-500 mt-1">{form.formState.errors.feature.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="preconditions">Preconditions</Label>
                    <Textarea
                      id="preconditions"
                      {...form.register("preconditions")}
                      placeholder="Prerequisites that must be met before executing this test"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="testSteps">Test Steps</Label>
                    <Textarea
                      id="testSteps"
                      {...form.register("testSteps")}
                      placeholder="1. Navigate to...&#10;2. Click on...&#10;3. Verify that..."
                      rows={4}
                    />
                  </div>

                  <div>
                    <Label htmlFor="expectedResults">Expected Results</Label>
                    <Textarea
                      id="expectedResults"
                      {...form.register("expectedResults")}
                      placeholder="What should happen when the test is executed"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="estimatedTime">Estimated Time (minutes)</Label>
                    <Input
                      id="estimatedTime"
                      type="number"
                      {...form.register("estimatedTime", { valueAsNumber: true })}
                      placeholder="5"
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editingTestCase ? "Update Test Case" : "Create Test Case"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <TabsContent value={activeSubTab} className="mt-0">
          {isLoading ? (
            <div className="text-center py-8">Loading test cases...</div>
          ) : viewMode === "board" ? (
            <BoardView
              items={filteredTestCases}
              statuses={TESTCASE_STATUSES}
              onStatusChange={handleStatusChange}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onView={handleViewTestCase}
              onAdd={handleAddForStatus}
              type="testcase"
            />
          ) : (
            <ListView
              items={filteredTestCases}
              statuses={TESTCASE_STATUSES}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onStatusChange={handleStatusChange}
              onView={handleViewTestCase}
              type="testcase"
            />
          )}
        </TabsContent>
      </Tabs>
      <Dialog open={isDetailDialogOpen} onOpenChange={(open) => !open && closeTestCaseDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedTestCase?.title || "Test Case Details"}</DialogTitle>
            <DialogDescription>
              {selectedTestCase?.description || "No additional description provided."}
            </DialogDescription>
          </DialogHeader>
          {selectedTestCase && (
            <div className="space-y-3 py-2 text-sm text-gray-700">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase text-gray-500">Feature</p>
                  <p className="font-medium">{selectedTestCase.feature}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Status</p>
                  <p className="font-medium">{selectedTestCase.status}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Priority</p>
                  <p className="font-medium">{selectedTestCase.priority}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Type</p>
                  <p className="font-medium">{selectedTestCase.type || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Automation</p>
                  <p className="font-medium">{selectedTestCase.automationStatus}</p>
                </div>
                {selectedTestCase.estimatedTime !== undefined && (
                  <div>
                    <p className="text-xs uppercase text-gray-500">Estimated Time</p>
                    <p className="font-medium">{selectedTestCase.estimatedTime}m</p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Preconditions</p>
                <p className="font-medium whitespace-pre-line">
                  {selectedTestCase.preconditions || "Not provided"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Test Steps</p>
                <p className="font-medium whitespace-pre-line">
                  {selectedTestCase.testSteps || "Not provided"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Expected Results</p>
                <p className="font-medium whitespace-pre-line">
                  {selectedTestCase.expectedResults || "Not provided"}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={closeTestCaseDialog}>
              Close
            </Button>
            <Button
              onClick={() => {
                if (selectedTestCase) {
                  handleEdit(selectedTestCase);
                }
                closeTestCaseDialog();
              }}
            >
              Edit Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Test Runs Tab with View Toggle
function TestRunsTab() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTestRun, setEditingTestRun] = useState<TestRun | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"board" | "list">("board");
  const [activeSubTab, setActiveSubTab] = useState("all");

  const { data: testRuns = [], isLoading } = useQuery<TestRun[]>({
    queryKey: ["/api/testruns"],
  });

  const form = useForm<TestRunForm>({
    resolver: zodResolver(testRunSchema),
    defaultValues: {
      name: "",
      description: "",
      environment: "development",
      status: "planned",
      version: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: TestRunForm) => {
      const response = await apiRequest("POST", "/api/testruns", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testruns"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({ title: "Test run created successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to create test run", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<TestRunForm> }) => {
      const response = await apiRequest("PUT", `/api/testruns/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testruns"] });
      setEditingTestRun(null);
      form.reset();
      toast({ title: "Test run updated successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to update test run", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/testruns/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testruns"] });
      toast({ title: "Test run deleted successfully" });
    },
    onError: (error) => {
      toast({ title: "Failed to delete test run", description: error.message, variant: "destructive" });
    },
  });

  const onSubmit = (data: TestRunForm) => {
    if (editingTestRun) {
      updateMutation.mutate({ id: editingTestRun.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (testRun: TestRun) => {
    setEditingTestRun(testRun);
    form.reset({
      name: testRun.name,
      description: testRun.description || "",
      environment: testRun.environment,
      status: testRun.status,
      version: testRun.version || "",
    });
    setIsCreateDialogOpen(true);
  };

  const handleStatusChange = (testRunId: number, newStatus: string) => {
    updateMutation.mutate({ id: testRunId, data: { status: newStatus } });
  };

  const filteredTestRuns = useMemo(() => {
    return testRuns.filter((testRun) => {
      const matchesSearch = testRun.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           testRun.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = activeSubTab === "all" || testRun.status === activeSubTab;
      return matchesSearch && matchesStatus;
    });
  }, [testRuns, searchTerm, activeSubTab]);

  return (
    <div className="space-y-4">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {TESTRUN_STATUSES.map(status => (
              <TabsTrigger key={status.value} value={status.value}>
                {status.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === "board" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("board")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingTestRun(null); form.reset(); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Test Run
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingTestRun ? "Edit Test Run" : "Create Test Run"}</DialogTitle>
                  <DialogDescription>
                    {editingTestRun ? "Update the test run details." : "Plan a new test execution run."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Test Run Name</Label>
                    <Input
                      id="name"
                      {...form.register("name")}
                      placeholder="Sprint 1 Regression Testing"
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-red-500 mt-1">{form.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      {...form.register("description")}
                      placeholder="Optional description of the test run"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="environment">Environment</Label>
                      <Select onValueChange={(value) => form.setValue("environment", value)} value={form.watch("environment")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select environment" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="development">Development</SelectItem>
                          <SelectItem value="staging">Staging</SelectItem>
                          <SelectItem value="production">Production</SelectItem>
                          <SelectItem value="qa">QA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select onValueChange={(value) => form.setValue("status", value)} value={form.watch("status")}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {TESTRUN_STATUSES.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="version">Version</Label>
                    <Input
                      id="version"
                      {...form.register("version")}
                      placeholder="v1.0.0"
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                      {editingTestRun ? "Update Test Run" : "Create Test Run"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <TabsContent value={activeSubTab} className="mt-0">
          {isLoading ? (
            <div className="text-center py-8">Loading test runs...</div>
          ) : viewMode === "board" ? (
            <BoardView
              items={filteredTestRuns}
              statuses={TESTRUN_STATUSES}
              onStatusChange={handleStatusChange}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              type="testrun"
            />
          ) : (
            <ListView
              items={filteredTestRuns}
              statuses={TESTRUN_STATUSES}
              onEdit={handleEdit}
              onDelete={(id) => deleteMutation.mutate(id)}
              onStatusChange={handleStatusChange}
              type="testrun"
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Main Roadmap component
export default function Roadmap() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("roadmap");

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Please log in to access the roadmap.</p>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8">
      <div className="mb-8 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Project Management
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Manage features, bugs, test cases, and test runs for the Prize Drop platform.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6 w-full">
        <TabsList className="grid w-full max-w-7xl mx-auto grid-cols-3">
          <TabsTrigger value="roadmap">Roadmap</TabsTrigger>
          <TabsTrigger value="bugs">Bugs</TabsTrigger>
          <TabsTrigger value="testcases">Test Cases</TabsTrigger>
        </TabsList>

        <TabsContent value="roadmap">
          <RoadmapItemsTab />
        </TabsContent>

        <TabsContent value="bugs">
          <BugsTab />
        </TabsContent>

        <TabsContent value="testcases">
          <TestCasesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
